import type { ParameterOption, ParameterType } from '@shared/types';

export type CodeParseResult = {
  type: ParameterType;
  value: string;
  options?: ParameterOption[];
};

/**
 * Detects a `code` parameter — a single-line OpenSCAD expression stored as a
 * quoted string with the `[code]` annotation in the comment.
 *
 * Basic syntax:
 *   multiplier = "i * 2"; // [code]
 *
 * With preset options (plain — expression used as both value and label):
 *   offset = "i * 2"; // [code] [i*2, i*3, i/2]
 *
 * With preset options (labelled — separate display label):
 *   offset = "i * 2"; // [code] [i*2:Double, i*3:Triple, i/2:Half]
 *
 * Mixed labelled/plain tokens are also supported:
 *   offset = "i * 2"; // [code] [i*2:Double, i*3, i/2:Half]
 *
 * The expression is stored as a plain string in the Parameter value field.
 * Before compilation the declaration is replaced with a real SCAD assignment
 * so that `multiplier = i * 2;` is sent to the OpenSCAD compiler instead.
 *
 * Returns null if the comment does not contain `[code]` or the value is not
 * a quoted string.
 */
export function parseCodeType(
  valueStr: string,
  comment: string,
): CodeParseResult | null {
  // Must have [code] annotation in the comment
  if (!/\[code\]/i.test(comment)) return null;

  // Value must be a quoted string
  if (!valueStr.startsWith('"') || !valueStr.endsWith('"')) return null;

  // Unwrap the quotes to get the raw expression text
  const expression = valueStr.slice(1, -1);

  // Strip the [code] annotation, then look for an options list [opt1, opt2, ...]
  const cleanedComment = comment.replace(/\[code\]/i, '').trim();
  const options = parseCodeOptions(cleanedComment);

  return { type: 'code', value: expression, options };
}

/**
 * Parse an optional `[opt1, opt2]` or `[val:Label, ...]` options list from
 * the comment text (after the `[code]` annotation has been removed).
 *
 * Returns undefined when no options block is found.
 *
 * Labelled syntax: each token contains `:` with a non-empty right side.
 *   e.g. `[i*2:Double, i*3:Triple]` → [{ value: 'i*2', label: 'Double' }, ...]
 *
 * Plain syntax: token is used as both value and label.
 *   e.g. `[i*2, i*3]` → [{ value: 'i*2', label: 'i*2' }, ...]
 *
 * Mixed: tokens without a label part use the expression as label.
 */
function parseCodeOptions(comment: string): ParameterOption[] | undefined {
  const optionsMatch = comment.match(/\[([^\]]+)\]/);
  if (!optionsMatch) return undefined;

  const rawTokens = optionsMatch[1].split(',').map((s) => s.trim()).filter(Boolean);
  if (rawTokens.length === 0) return undefined;

  return rawTokens.map((tok) => {
    const colonIdx = tok.indexOf(':');
    if (colonIdx !== -1) {
      const valuePart = tok.slice(0, colonIdx).trim();
      const labelPart = tok.slice(colonIdx + 1).trim();
      if (labelPart.length > 0) {
        return { value: valuePart, label: labelPart };
      }
    }
    // No label (or empty label after colon) — use expression as label
    return { value: tok, label: tok };
  });
}
